import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../utilities/axios";
import { defaultDate, generateMonthList } from "../../utilities/filters";
import {
  groupElementsByDate,
  makeElementsEmpty,
} from "../../utilities/groupElementsByDate";
import { groupMonths } from "../../utilities/regroupment";
import {
  addIdKeyToCategories,
  calculateMonthTotals,
  createArrayOfObjects,
  getElementsPerRange,
  updateSynthesis,
} from "../../utilities/table";
import { addRacineToCatgories } from "../../utilities/tableUtils";
import { getElementsOfObjective, getOneObjective } from "./objectives";
import { openDeleteDialog } from "./table";

const { startDate, endDate } = defaultDate();

const initialState = {
  categories: [],
  categoriesOriginal: [],
  showOnlyValidatedCompta: false,
  category: null,
  selectedMonths: generateMonthList(startDate, endDate),
  filtersValues: {
    status: [],
    type: [],
  },
  showHiddenValues: false,
  levelOneNotMentioned: false,
  footerComptaFilter: { filterOptions: {} },
  footerOptionsTreso: { option: {}, account: {} },
  accountTreso: [],
  footerAccountTreso: { filterOptions: {} },
  footerOptionCompta: { option: {} },
  filterValuesCompta: [],
  categoryStatus: "idle",
  noFilterCheckedCompta: true,
  noFilterChecked: true,
  statusTofilter: null,
  typeToFilter: null,
  categoriesForSelect: [],
  status: "idle",
  exportStatus: "idle",
  createStatus: "idle",
  isDragStarted: false,
  createError: null,
  error: null,
  synthesisArr: [],
  getSynthesisStatus: "idle",
  synthesis: [],
  editStatus: "idle",
  editError: "",
  categoryToEdit: "",
  footerFiltersStatus: {
    status: "pointé",
  },
  footerFiltersTypes: {
    type: "simulation,réel",
  },
  RegroupmentValue: 1,
  parentKey: "",
  simulationElement: null,
  isCreatingInSimulation: false,
};

export const exportCategories = createAsyncThunk(
  "categories/exportCategories",
  async (queries, thunkAPI) => {
    try {
      const response = await axios({
        method: "get",
        url: `/api/projects/${queries.projectId}/${
          queries.isPlancomptable ? "plan_comptable" : "categories"
        }/${queries.mode}/export`,
        params: { ...queries.params, at: new Date().toLocaleString() },
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "categories.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);
      if (response.status === 200) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const getCategories = createAsyncThunk(
  "categories/getCategories",
  async (queries, thunkAPI) => {
    const nofilterChecked =
      !queries.params?.noFilterChecked &&
      queries.params?.filterOptions?.length === 0;

    if (queries.params) {
      delete queries?.params?.noFilterChecked;
    }

    let data;

    try {
      const response = await axios.get(
        `/api/projects/${queries.projectId}/categories/${queries.mode}`,
        {
          params: {
            ...queries.params,
          },
        }
      );

      data = await response.data;
      if (response.status === 200) {
        const arrayWithfilters = nofilterChecked
          ? makeElementsEmpty(data.data)
          : data.data;

        const transformedData = groupElementsByDate(
          arrayWithfilters,
          queries.mode
        );

        return transformedData;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const getSynthesis = createAsyncThunk(
  "categories/getSynthesis",
  async (queries, thunkAPI) => {
    const nofilterChecked =
      !queries.params?.noFilterChecked &&
      queries.params?.filterOptions?.length === 0;

    if (queries.params) {
      delete queries?.params?.noFilterChecked;
    }

    let data;
    try {
      const response = await axios.get(
        `/api/projects/${queries.projectId}/categories/${queries.mode}/synthese/`,
        { params: queries.params }
      );
      data = await response.data;

      if (
        queries?.mode === "compta" &&
        queries?.objectiveId &&
        !queries?.isListing
      ) {
        thunkAPI.dispatch(getElementsOfObjective(queries?.objective));
      }

      if (response.status === 200) {
        return nofilterChecked
          ? data.data.map((el) => (el.synthesis = 0))
          : data.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const getOneCategory = createAsyncThunk(
  "categories/getOneCategory",
  async (queries, thunkAPI) => {
    let data;
    try {
      const response = await axios.get(
        `api/categories/${queries.categoryId}/${queries.projectId}`
      );
      data = await response.data;
      if (response.status === 200) {
        return {
          data: data,
          message: data.message,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(err.message ? err.message : err);
    }
  }
);

export const createCategory = createAsyncThunk(
  "categories/createCategory",
  async (categoryData, thunkAPI) => {
    let data;
    try {
      const response = await axios.post(
        `/api/projects/${categoryData?.project?.projectId}/category`,
        categoryData?.values
      );
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(deleteInput());
        thunkAPI.dispatch(getCategories(categoryData?.project)).then(() => {
          thunkAPI.dispatch(getSynthesis(categoryData?.synthesis));

          if (
            categoryData?.mode === "compta" &&
            categoryData?.objectiveId &&
            !categoryData?.isListing
          ) {
            thunkAPI.dispatch(
              getElementsOfObjective(categoryData?.objectiveId)
            );
          }
        });
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error({ err });
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const editCategory = createAsyncThunk(
  "categories/editCategory",
  async (values, thunkAPI) => {
    let data;
    try {
      const response = await axios.put(
        `/api/categories/${values.categoryId}`,
        values.values
      );
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getCategoryToEdit(""));
        thunkAPI.dispatch(getCategories(values.project)).then(() => {
          if (values.isSynthesis) {
            thunkAPI.dispatch(getSynthesis(values?.synthesis));
          }
          if (
            values?.mode === "compta" &&
            values?.objectiveId &&
            !values?.isListing
          ) {
            thunkAPI.dispatch(
              getElementsOfObjective({
                objectiveId: values.objectiveId,
                isPlancompta: values.isPlancompta,
              })
            );
            thunkAPI.dispatch(getOneObjective(values?.objectiveId));
          }
        });

        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "categories/deleteCategory",
  async (values, thunkAPI) => {
    let data;

    try {
      const response = await axios.delete(
        `/api/categories/${values.categoryId}`
      );
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(openDeleteDialog(null));
        thunkAPI.dispatch(getCategories(values?.project)).then(() => {
          thunkAPI.dispatch(getSynthesis(values?.synthesis));

          if (
            values?.mode === "compta" &&
            values?.objectiveId &&
            !values?.isListing
          ) {
            thunkAPI.dispatch(getElementsOfObjective(values?.objective));
          }
        });

        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error({ err });
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const assignPlanComptable = createAsyncThunk(
  "categories/assignPlanComptable",
  async (values, thunkAPI) => {
    let data;

    try {
      const response = await axios.post(
        `/api/categories/${values.categoryId}/assign-plan-comptable`,
        {
          planComptableId: values.planComptableId,
          assignmentType: values.assignmentType,
        }
      );
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getCategories(values?.project));
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message || err);
    }
  }
);

const slice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    setTypeToFilter: (state, action) => {
      state.typeToFilter = action.payload;
    },
    setFilters: (state, action) => {
      state.filtersValues = action.payload ?? state.filtersValues;
    },
    setMonthList: (state, action) => {
      state.selectedMonths = action.payload;
    },
    setStatusToFilter: (state, action) => {
      state.statusTofilter = action.payload;
    },
    addInput: (state, action) => {
      state.categoriesOriginal.push(action.payload);
    },
    setParentKey: (state, action) => {
      state.parentKey = action.payload;
    },
    setDragStarted: (state, action) => {
      state.isDragStarted = action.payload;
    },
    setAccountsTresoWithTypesAndStatus: (state, action) => {
      state.accountTreso = action.payload.accountTreso;
      state.noFilterChecked = action.payload.noFilterChecked;
    },
    setShowOnlyValidatedCompta: (state, action) => {
      state.showOnlyValidatedCompta = action.payload;
    },
    setComptaFilters: (state, action) => {
      state.filterValuesCompta = action.payload.filterValuesCompta;
      state.noFilterCheckedCompta = action.payload.noFilterCheckedCompta;
    },
    setFooterAccountsTresoWithTypesAndStatus: (state, action) => {
      state.footerAccountTreso = action.payload.footerAccountTreso;
    },

    setFooterComptaFilter: (state, action) => {
      state.footerComptaFilter = action.payload;
    },
    setShowHiddenValues: (state, action) => {
      state.showHiddenValues = action.payload;
    },

    setFooterComptaOptions: (state, action) => {
      state.footerOptionCompta = action.payload;
    },

    setFooterOptions: (state, action) => {
      state.footerOptionsTreso = action.payload;
    },
    resetFilters: (state, action) => {
      // Preserve footer filters
      const preservedFooterOptionsTreso = { ...state.footerOptionsTreso };
      const preservedFooterAccountTreso = { ...state.footerAccountTreso };
      const preservedFooterOptionCompta = { ...state.footerOptionCompta };
      const preservedFooterComptaFilter = { ...state.footerComptaFilter };
      const preservedShowOnlyValidatedCompta = state.showOnlyValidatedCompta;

      // Preserve right filter values
      const preservedAccountTreso = [...state.accountTreso];
      const preservedFilterValuesCompta = [...state.filterValuesCompta];
      const preservedNoFilterChecked = state.noFilterChecked;
      const preservedNoFilterCheckedCompta = state.noFilterCheckedCompta;

      // Reset flags
      state.noFilterCheckedCompta = true;
      state.noFilterChecked = true;

      if (
        Object.keys(state.footerOptionsTreso.option).length > 0 ||
        Object.keys(state.footerOptionsTreso.account).length > 0
      ) {
        state.footerOptionsTreso = { option: {}, account: {} };
      }

      if (Object.keys(state.footerOptionCompta.option).length > 0) {
        state.footerOptionCompta = { option: {} };
      }

      if (Object.keys(state.footerComptaFilter.filterOptions).length > 0) {
        state.footerComptaFilter = { filterOptions: {} };
      }

      if (Object.keys(state.footerAccountTreso.filterOptions).length > 0) {
        state.footerAccountTreso = { filterOptions: {} };
      }

      const hadValidFooterOptionsTreso = 
        (preservedFooterOptionsTreso.option && Object.keys(preservedFooterOptionsTreso.option).length > 0) ||
        (preservedFooterOptionsTreso.account && Object.keys(preservedFooterOptionsTreso.account).length > 0);
      
      const hadValidFooterAccountTreso = 
        preservedFooterAccountTreso.filterOptions && 
        (Array.isArray(preservedFooterAccountTreso.filterOptions) 
          ? preservedFooterAccountTreso.filterOptions.length > 0 
          : Object.keys(preservedFooterAccountTreso.filterOptions).length > 0);

      if (hadValidFooterOptionsTreso) {
        state.footerOptionsTreso = preservedFooterOptionsTreso;
      }

      if (hadValidFooterAccountTreso) {
        state.footerAccountTreso = preservedFooterAccountTreso;
      }

      if (preservedFooterOptionCompta.option && Object.keys(preservedFooterOptionCompta.option).length > 0) {
        state.footerOptionCompta = preservedFooterOptionCompta;
      }

      if (preservedFooterComptaFilter.filterOptions && 
          (Array.isArray(preservedFooterComptaFilter.filterOptions) 
            ? preservedFooterComptaFilter.filterOptions.length > 0 
            : Object.keys(preservedFooterComptaFilter.filterOptions).length > 0)) {
        state.footerComptaFilter = preservedFooterComptaFilter;
      }

      if (preservedShowOnlyValidatedCompta) {
        state.showOnlyValidatedCompta = preservedShowOnlyValidatedCompta;
      }

      // Restore right filter values if they exist
      if (preservedAccountTreso.length > 0) {
        state.accountTreso = preservedAccountTreso;
        state.noFilterChecked = preservedNoFilterChecked;
      } else {
        state.accountTreso = [];
      }

      if (preservedFilterValuesCompta.length > 0) {
        state.filterValuesCompta = preservedFilterValuesCompta;
        state.noFilterCheckedCompta = preservedNoFilterCheckedCompta;
      } else {
        state.filterValuesCompta = [];
      }
    },

    setFooterFilters: (state, action) => {
      if (action.payload.mode === "treso") {
        state.footerFiltersStatus = action.payload.filters;
        return;
      }
      state.footerFiltersTypes = action.payload.filters;
    },
    addInputWithCertainIndex: (state, action) => {
      function addObjectByIdInArray(arr, targetId, newObj) {
        for (const obj of arr) {
          if (obj.id === targetId) {
            if (!obj.children) {
              obj.children = [];
            }

            if (obj.children.length > 0) {
              const lastChild = obj.children[obj.children.length - 1];
              if (lastChild.isInput) {
                obj.children.pop();
                continue;
              }
            }
            obj.children.push(newObj);
          } else if (obj.children) {
            addObjectByIdInArray(obj.children, targetId, newObj);
          }
        }
      }

      return addObjectByIdInArray(
        state.categoriesOriginal,
        action.payload.id,
        action.payload.newObj
      );
    },
    deleteInput: (state, action) => {
      if (
        state.categoriesOriginal[state.categoriesOriginal?.length - 1]?.isInput
      ) {
        state.categoriesOriginal.pop();
      }
    },
    setLevelOneNotMentioned: (state, action) => {
      state.levelOneNotMentioned = action.payload;
    },
    getCategoryToEdit: (state, action) => {
      state.categoryToEdit = action.payload;
    },

    setCategories: (state, action) => {
      state.categories = action.payload;
      state.categoriesOriginal = action.payload;
    },

    getCategoriesState: (state) => {
      return state.categories;
    },

    setRegroupmentValue: (state, action) => {
      state.RegroupmentValue = action.payload;
    },
    addElementInCategories: (state, action) => {
      function addObjectByIdInArray(arr, targetId) {
        for (const obj of arr) {
          if (obj.id === targetId) {
            if (!obj.children) {
              obj.children = [];
            }

            let indexOfLastChildWithoutName = -1;

            const existingCreateChildIndex = obj.children.findIndex(
              (child) => child.isCreate
            );

            if (existingCreateChildIndex === -1) {
              state.isCreatingInSimulation = true;
              if (obj.children.length > 0) {
                for (let i = obj.children.length - 1; i >= 0; i--) {
                  const child = obj.children[i];
                  if (!child.name) {
                    indexOfLastChildWithoutName = i;
                    break;
                  }
                }
              }

              if (indexOfLastChildWithoutName === -1) {
                obj.children.splice(0, 0, {
                  dataIndex: action.payload.dataIndex,
                  category: action.payload.category,
                  isCreate: true,
                });
              }

              if (indexOfLastChildWithoutName !== -1) {
                obj.children.splice(indexOfLastChildWithoutName + 1, 0, {
                  dataIndex: action.payload.dataIndex,
                  category: action.payload.category,
                  isCreate: true,
                });
              }
            } else {
              state.isCreatingInSimulation = false;
              obj.children.splice(existingCreateChildIndex, 1);
            }
          } else if (obj.children) {
            addObjectByIdInArray(obj.children, targetId);
          }
        }
      }

      return addObjectByIdInArray(
        state.categoriesOriginal,
        action.payload.category.id
      );
    },
  },
  extraReducers: {
    [exportCategories.pending]: (state) => {
      state.exportStatus = "loading";
    },
    [exportCategories.fulfilled]: (state) => {
      state.exportStatus = "succeeded";
    },
    [exportCategories.rejected]: (state) => {
      state.exportStatus = "failed";
    },
    [getCategories.pending]: (state, action) => {
      state.status = "loading";
    },
    [getCategories.fulfilled]: (state, action) => {
      state.status = "succeeded";
      const categoriesWithKeys = addIdKeyToCategories(action.payload);

      const categoriesWithKeysCopy = JSON.parse(
        JSON.stringify(categoriesWithKeys)
      );

      state.categoriesForSelect = categoriesWithKeysCopy;

      const categoriesWithRacine = addRacineToCatgories(categoriesWithKeys);

      const monthsGrouped = groupMonths(
        state.RegroupmentValue,
        state.selectedMonths
      );

      const categoriesWithSum = calculateMonthTotals(
        categoriesWithRacine,
        monthsGrouped
      );

      function processCategory(category) {
        const currentCategory = { ...category };
        if (currentCategory.elements) {
          const elementsPerRange = getElementsPerRange(
            monthsGrouped,
            currentCategory.elements
          );

          const finalElements = createArrayOfObjects(elementsPerRange);

          if (currentCategory.children) {
            const childrenArray = [...currentCategory.children];
            const reversedArrayElements = finalElements.toReversed();

            for (let i = 0; i < reversedArrayElements.length; i++) {
              const { children, ...category } = currentCategory;
              childrenArray.unshift({
                ...reversedArrayElements[i],
                id: `${currentCategory.id}-${i}`,
                isForelements: true,
                category,
                isMasked: category.isMasked,
              });
            }
            currentCategory.children = childrenArray.map((childCategory) =>
              processCategory(childCategory)
            );
          }
        }
        return currentCategory;
      }

      const processedCategories = categoriesWithSum.map(processCategory);

      if (state.synthesisArr && state.synthesisArr.length > 0) {
        state.categoriesOriginal = updateSynthesis(processedCategories, state.synthesisArr);
        state.categories = updateSynthesis(processedCategories, state.synthesisArr);
      } else {
        state.categoriesOriginal = processedCategories;
        state.categories = processedCategories;
      }
    },
    [getCategories.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
    [getSynthesis.pending]: (state, action) => {
      state.synthesisStatus = "loading";
    },
    [getSynthesis.fulfilled]: (state, action) => {
      const categoriesWithSynthesis = updateSynthesis(state.categories, action.payload);
      const categoriesOriginalWithSynthesis = updateSynthesis(
        state.categoriesOriginal,
        action.payload
      );
      state.categories = categoriesWithSynthesis;
      state.categoriesOriginal = categoriesOriginalWithSynthesis;
      state.synthesisStatus = "succeeded";
      state.synthesisArr = action.payload;
    },
    [getSynthesis.rejected]: (state, action) => {
      state.synthesisStatus = "failed";
      state.error = action.payload;
    },

    [getOneCategory.pending]: (state) => {
      state.categoryStatus = "loading";
    },
    [getOneCategory.fulfilled]: (state, action) => {
      state.categoryStatus = "succeeded";
      state.category = action.payload.data.data;
    },
    [getOneCategory.rejected]: (state) => {
      state.categoryStatus = "failed";
    },
    [createCategory.pending]: (state, action) => {
      state.createStatus = "loading";
    },
    [createCategory.fulfilled]: (state, action) => {
      state.createStatus = "succeeded";
    },
    [createCategory.rejected]: (state, action) => {
      state.createStatus = "failed";
      state.createError = action.payload;
    },
    [editCategory.pending]: (state, action) => {
      state.editStatus = "loading";
    },
    [editCategory.rejected]: (state, action) => {
      state.editStatus = "failed";
    },
  },
});

export const {
  setStatusToFilter,
  setTypeToFilter,
  addInput,
  deleteInput,
  getCategoryToEdit,
  addInputWithCertainIndex,
  setParentKey,
  setCategories,
  getCategoriesState,
  addElementInCategories,
  setFilters,
  setDragStarted,
  resetFilters,
  setMonthList,
  setFooterComptaFilter,
  setShowHiddenValues,
  setRegroupmentValue,
  setFooterFilters,
  setFooterAccountsTresoWithTypesAndStatus,
  setAccountsTresoWithTypesAndStatus,
  setComptaFilters,
  setShowOnlyValidatedCompta,
  setFooterOptions,
  setFooterComptaOptions,
  setLevelOneNotMentioned,
} = slice.actions;
export const reducer = slice.reducer;

export default slice;
